import React from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AppDatePicker from '../../common/AppDatePicker';

const ACTION_TYPES = [
  { value: 'skip_row', label: 'Skip Row', description: 'Skip entire row if condition is met' },
  { value: 'fail_row', label: 'Fail Row', description: 'Checked against the table when the file is approved: fails the file' },
  { value: 'warn_row', label: 'Warn', description: 'Checked against the table when the file is approved: records a warning' },
  { value: 'trim', label: 'Trim', description: 'Remove leading/trailing whitespace' },
  { value: 'remove_spaces', label: 'Remove Spaces', description: 'Remove all spaces from the value' },
  { value: 'set_value', label: 'Set Value', description: 'Set cell to a specific value' },
  { value: 'concatenate', label: 'Concatenate', description: 'Combine multiple columns into text' },
  { value: 'transform_value', label: 'Transform', description: 'Modify the cell using common type-aware transforms' },
];

const VERDICT_ACTIONS = new Set(['skip_row', 'fail_row', 'warn_row']);

// Changing the action type starts the new type's configuration empty, carrying over
// only the two fields that mean the same thing whichever action reads them: the
// target column, and the message the three verdict actions share.
//
// Every other field belongs to the type that owned it, and a field name shared by
// two types does NOT make its value portable — `transformation` is the one that bit:
// merging the type change into the old action left a Transform Value's
// `transformation: 'left'` on a Concatenate, whose editor cannot show it and whose
// runtime applies it with no parameters, so the count read as zero and every matched
// value was replaced with an empty string.
const actionForType = (action, nextType) => {
  const previous = action || {};
  const next = { type: nextType };
  if (previous.column !== undefined) next.column = previous.column;
  if (VERDICT_ACTIONS.has(nextType) && VERDICT_ACTIONS.has(previous.type)
      && previous.message !== undefined) {
    next.message = previous.message;
  }
  return next;
};

const FORMULA_TYPES = [
  { value: 'prepend', label: 'Prepend from Column' },
  { value: 'append', label: 'Append from Column' },
  { value: 'arithmetic', label: 'Arithmetic Operation' },
];

const NUMBER_ARITHMETIC_OPERATIONS = [
  { value: 'multiply', label: 'Multiply by' },
  { value: 'divide', label: 'Divide by' },
  { value: 'add', label: 'Add' },
  { value: 'subtract', label: 'Subtract' },
];

const DATE_ARITHMETIC_OPERATIONS = [
  { value: 'add', label: 'Add Days' },
  { value: 'subtract', label: 'Subtract Days' },
];

const arithmeticOperationsFor = (declaredType) => (
  declaredType === 'Date' ? DATE_ARITHMETIC_OPERATIONS : NUMBER_ARITHMETIC_OPERATIONS
);

// An operation the target type can run, else that type's default. It is stored into the rule,
// never only displayed: the runtime reads formula_config.operation and fails without it (M234).
const resolveArithmeticOperation = (declaredType, operation) => (
  arithmeticOperationsFor(declaredType).some((option) => option.value === operation)
    ? operation
    : (declaredType === 'Date' ? 'add' : 'multiply')
);

function normalizeArithmeticAction(action, targetColumn, columnTypes) {
  if (action.value_source !== 'formula' || action.formula_type !== 'arithmetic') return action;
  const targetType = columnTypes[targetColumn] || 'Text';
  const config = { ...action.formula_config };
  config.operation = resolveArithmeticOperation(targetType, config.operation);
  config.source_mode = config.source_mode || 'target';
  config.operand_source = config.operand_source || 'static';
  if (config.source_mode !== 'column') config.source_column = '';
  const allowedSourceTypes = targetType === 'Number' && config.operation === 'subtract'
    ? ['Number', 'Date'] : [targetType];
  if (config.source_column && (
    config.source_column === targetColumn || !allowedSourceTypes.includes(columnTypes[config.source_column])
  )) {
    config.source_column = '';
  }

  const sourceType = config.source_column ? columnTypes[config.source_column] : targetType;
  const operandType = targetType === 'Number' && config.operation === 'subtract' && sourceType === 'Date'
    ? 'Date' : 'Number';
  if (config.operand_source !== 'column' || (
    config.operand_column && columnTypes[config.operand_column] !== operandType
  )) {
    config.operand_column = '';
  }
  // Types come from the selected columns; storing another copy lets them drift.
  delete config.source_type;
  delete config.operand_type;
  return { ...action, formula_config: config };
}

const TEXT_TRANSFORM_OPTIONS = [
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'lowercase', label: 'Lowercase' },
  { value: 'trim', label: 'Trim Whitespace' },
  { value: 'remove_spaces', label: 'Remove Spaces' },
  { value: 'strip_leading_zeros', label: 'Strip Leading Zeros' },
  { value: 'keep_digits', label: 'Keep Digits Only' },
  { value: 'remove_characters', label: 'Remove Characters' },
  { value: 'left', label: 'Left (first N)' },
  { value: 'right', label: 'Right (last N)' },
  { value: 'mid', label: 'Substring (start, length)' },
  { value: 'replace', label: 'Find & Replace' },
  { value: 'pad_left', label: 'Pad Left' },
  { value: 'pad_right', label: 'Pad Right' },
  { value: 'drop_prefix', label: 'Drop Prefix (text)' },
  { value: 'drop_suffix', label: 'Drop Suffix (text)' },
  { value: 'cast_number_string', label: 'Cast to Number String' },
];

const TARGET_REQUIRED_ACTIONS = new Set(['trim', 'remove_spaces', 'set_value', 'concatenate', 'transform_value']);

const CellActionBuilder = ({
  action,
  onUpdateAction,
  availableColumns,
  targetColumn,
  columnTypeMap = {},
  allowTargetColumnSelection = false,
}) => {
  const effectiveTargetColumn = allowTargetColumnSelection ? (action.column || targetColumn || '') : (targetColumn || '');
  const targetType = columnTypeMap[effectiveTargetColumn] || 'Text';
  const otherColumns = availableColumns.filter((column) => column && column !== effectiveTargetColumn);

  const updateAction = (updates) => {
    const nextAction = normalizeArithmeticAction({ ...action, ...updates }, effectiveTargetColumn, columnTypeMap);
    onUpdateAction(nextAction);
  };

  const getTransformOptionsForType = (declaredType) => {
    if (declaredType === 'Date') {
      return [{ value: 'format_date', label: 'Format Date' }];
    }
    if (declaredType === 'Number') {
      return [{ value: 'round_number', label: 'Round Number' }];
    }
    if (declaredType === 'Text') {
      return TEXT_TRANSFORM_OPTIONS;
    }
    return [];
  };

  const getSetValueMode = () => {
    if (action.value_source === 'formula') return 'formula';
    if (action.value_type === 'column') return 'column';
    return 'static';
  };

  const getTextSourceColumns = () => otherColumns;

  const getCompatibleColumnsForTargetType = () => otherColumns.filter((column) => columnTypeMap[column] === targetType);

  const getArithmeticOperationOptions = () => arithmeticOperationsFor(targetType);

  const getArithmeticBaseColumns = (config = action.formula_config || {}) => {
    if (targetType === 'Date') {
      return otherColumns.filter((column) => columnTypeMap[column] === 'Date');
    }

    if (targetType === 'Number') {
      if ((config.operation || 'multiply') === 'subtract') {
        return otherColumns.filter((column) => ['Number', 'Date'].includes(columnTypeMap[column]));
      }
      return otherColumns.filter((column) => columnTypeMap[column] === 'Number');
    }

    return [];
  };

  const getArithmeticSourceType = (config = action.formula_config || {}) => {
    if (config.source_column) {
      return columnTypeMap[config.source_column] || targetType;
    }
    return targetType;
  };

  const getArithmeticOperandType = (config = action.formula_config || {}) => {
    if (targetType === 'Date') {
      return 'Number';
    }

    if (targetType === 'Number') {
      const sourceType = getArithmeticSourceType(config);
      const operation = config.operation || 'multiply';
      if (operation === 'subtract' && sourceType === 'Date') {
        return 'Date';
      }
      return 'Number';
    }

    return targetType;
  };

  const syncArithmeticConfig = (partialConfig) => {
    updateAction({ formula_config: { ...action.formula_config, ...partialConfig } });
  };

  const handleTargetColumnChange = (nextColumn) => {
    const nextType = columnTypeMap[nextColumn] || 'Text';
    const nextAction = { ...action, column: nextColumn };

    if (nextAction.type === 'transform_value') {
      const availableTransforms = getTransformOptionsForType(nextType).map((option) => option.value);
      if (!availableTransforms.includes(nextAction.transformation)) {
        nextAction.transformation = '';
        nextAction.transformation_params = {};
      }
    }

    if (nextAction.type === 'concatenate' && nextType !== 'Text') {
      nextAction.source_columns = [];
    }

    if (nextAction.type === 'set_value') {
      if (nextAction.value_type === 'column' && nextAction.source_column && columnTypeMap[nextAction.source_column] !== nextType) {
        nextAction.source_column = '';
      }
    }

    onUpdateAction(normalizeArithmeticAction(nextAction, nextColumn, columnTypeMap));
  };

  const handleSetValueModeChange = (nextMode) => {
    if (nextMode === 'formula') {
      const formulaType = action.formula_type || (targetType === 'Text' ? 'prepend' : 'arithmetic');
      const formulaConfig = {
        ...(action.formula_config || {}),
        source_mode: action.formula_config?.source_mode || 'target',
      };
      if (formulaType === 'arithmetic') {
        formulaConfig.operation = resolveArithmeticOperation(targetType, formulaConfig.operation);
        formulaConfig.operand_source = formulaConfig.operand_source || 'static';
      }
      updateAction({
        value_source: 'formula',
        value_type: 'static',
        source_column: '',
        formula_type: formulaType,
        formula_config: formulaConfig,
      });
      return;
    }

    updateAction({
      value_source: 'static',
      value_type: nextMode,
      ...(nextMode === 'column' ? {} : { source_column: '' }),
    });
  };

  const handleFormulaTypeChange = (nextType) => {
    if (nextType === 'arithmetic') {
      const nextConfig = {
        ...(action.formula_config || {}),
        operation: resolveArithmeticOperation(targetType, action.formula_config?.operation),
        source_mode: action.formula_config?.source_mode || 'target',
        operand_source: action.formula_config?.operand_source || 'static',
      };
      updateAction({
        formula_type: nextType,
        formula_config: nextConfig,
      });
      return;
    }

    updateAction({ formula_type: nextType });
  };

  const renderLiteralInput = (declaredType, label, value, onChange) => {
    if (declaredType === 'Date') {
      return (
        <AppDatePicker
          fullWidth
          label={label}
          value={value}
          onChange={(nextDate) => onChange(nextDate || null)}
          slotProps={{ textField: { sx: { mb: 2 } } }}
        />
      );
    }

    if (declaredType === 'Number') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={label}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mb: 2 }}
        />
      );
    }

    if (declaredType === 'Yes/No') {
      return (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={value === true ? 'true' : value === false ? 'false' : ''}
            onChange={(e) => {
              if (e.target.value === '') {
                onChange(null);
                return;
              }
              onChange(e.target.value === 'true');
            }}
            label={label}
          >
            <MenuItem value="">
              <em>Unset</em>
            </MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        sx={{ mb: 2 }}
      />
    );
  };

  const filteredFormulaTypes = FORMULA_TYPES.filter((type) => {
    if (targetType === 'Text') return ['prepend', 'append'].includes(type.value);
    if (targetType === 'Date' || targetType === 'Number') return type.value === 'arithmetic';
    return false;
  });

  const transformOptions = getTransformOptionsForType(targetType);
  const arithmeticOperandType = getArithmeticOperandType();
  const arithmeticBaseColumns = getArithmeticBaseColumns(action.formula_config || {});
  const arithmeticOperandColumns = otherColumns.filter((column) => columnTypeMap[column] === arithmeticOperandType);
  const needsTargetColumn = TARGET_REQUIRED_ACTIONS.has(action.type);

  return (
    <Box>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Action Type</InputLabel>
        <Select
          value={action.type || 'skip_row'}
          onChange={(e) => onUpdateAction(actionForType(action, e.target.value))}
          label="Action Type"
        >
          {ACTION_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              <Box>
                <Typography variant="body2">{type.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {type.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {allowTargetColumnSelection && needsTargetColumn && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Target Column</InputLabel>
          <Select
            value={effectiveTargetColumn}
            onChange={(e) => handleTargetColumnChange(e.target.value)}
            label="Target Column"
          >
            <MenuItem value="">
              <em>Select Column</em>
            </MenuItem>
            {availableColumns
              .filter((column) => action.type !== 'concatenate' || columnTypeMap[column] === 'Text')
              .map((column) => (
                <MenuItem key={column} value={column}>{column}</MenuItem>
              ))}
          </Select>
        </FormControl>
      )}

      {needsTargetColumn && !effectiveTargetColumn && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a target column to configure this action.
        </Alert>
      )}

      {action.type === 'fail_row' && (
        <TextField
          fullWidth
          size="small"
          label="Error Message"
          value={action.message || ''}
          onChange={(e) => updateAction({ message: e.target.value })}
          placeholder={effectiveTargetColumn ? `Invalid value in ${effectiveTargetColumn}` : 'Invalid value'}
          helperText="This message will be shown when the rule fails"
          sx={{ mb: 2 }}
        />
      )}

      {action.type === 'warn_row' && (
        <TextField
          fullWidth
          size="small"
          label="Warning Message"
          value={action.message || ''}
          onChange={(e) => updateAction({ message: e.target.value })}
          placeholder={effectiveTargetColumn ? `Suspicious value in ${effectiveTargetColumn}` : 'Suspicious value'}
          helperText="This warning will be recorded but processing will continue"
          sx={{ mb: 2 }}
        />
      )}

      {effectiveTargetColumn && action.type === 'set_value' && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Value Source</InputLabel>
            <Select
              value={getSetValueMode()}
              onChange={(e) => handleSetValueModeChange(e.target.value)}
              label="Value Source"
            >
              <MenuItem value="static">Fixed Value</MenuItem>
              <MenuItem value="column">From Column</MenuItem>
              {filteredFormulaTypes.length > 0 && <MenuItem value="formula">Formula</MenuItem>}
            </Select>
          </FormControl>

          {getSetValueMode() === 'static' && (
            renderLiteralInput(targetType, 'Value', action.value ?? action.static_value, (nextValue) => updateAction({
              value: nextValue,
              static_value: nextValue,
            }))
          )}

          {getSetValueMode() === 'column' && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Source Column</InputLabel>
              <Select
                value={action.source_column || ''}
                onChange={(e) => updateAction({ source_column: e.target.value })}
                label="Source Column"
              >
                <MenuItem value="">
                  <em>Select Column</em>
                </MenuItem>
                {getCompatibleColumnsForTargetType().map((column) => (
                  <MenuItem key={column} value={column}>{column}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {getSetValueMode() === 'formula' && (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Formula Type</InputLabel>
                <Select
                  value={action.formula_type || ''}
                  onChange={(e) => handleFormulaTypeChange(e.target.value)}
                  label="Formula Type"
                >
                  {filteredFormulaTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(action.formula_type === 'prepend' || action.formula_type === 'append') && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: alpha('#4e85eb', 0.02) }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Source Column</InputLabel>
                        <Select
                          value={action.formula_config?.source_column || ''}
                          onChange={(e) => updateAction({
                            formula_config: {
                              ...(action.formula_config || {}),
                              source_column: e.target.value,
                            },
                          })}
                          label="Source Column"
                        >
                          <MenuItem value="">
                            <em>Select Column</em>
                          </MenuItem>
                          {getTextSourceColumns().map((column) => (
                            <MenuItem key={column} value={column}>{column}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Separator"
                        value={action.formula_config?.separator || ''}
                        onChange={(e) => updateAction({
                          formula_config: {
                            ...(action.formula_config || {}),
                            separator: e.target.value,
                          },
                        })}
                        placeholder="-"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info" icon={<InfoOutlinedIcon />}>
                        {action.formula_type === 'prepend'
                          ? `Result: [${action.formula_config?.source_column || 'Source Column'}]${action.formula_config?.separator || ''}[${effectiveTargetColumn}]`
                          : `Result: [${effectiveTargetColumn}]${action.formula_config?.separator || ''}[${action.formula_config?.source_column || 'Source Column'}]`}
                      </Alert>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {action.formula_type === 'arithmetic' && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: alpha('#4e85eb', 0.02) }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Base Value</InputLabel>
                        <Select
                          value={action.formula_config?.source_mode || (action.formula_config?.source_column ? 'column' : 'target')}
                          onChange={(e) => syncArithmeticConfig({
                            source_mode: e.target.value,
                            source_column: e.target.value === 'column' ? (action.formula_config?.source_column || '') : '',
                          })}
                          label="Base Value"
                        >
                          <MenuItem value="target">Target Column</MenuItem>
                          <MenuItem value="column">Other Column</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {(action.formula_config?.source_mode || (action.formula_config?.source_column ? 'column' : 'target')) === 'column' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Source Column</InputLabel>
                          <Select
                            value={action.formula_config?.source_column || ''}
                            onChange={(e) => syncArithmeticConfig({ source_column: e.target.value })}
                            label="Source Column"
                          >
                            <MenuItem value="">
                              <em>Select Column</em>
                            </MenuItem>
                            {arithmeticBaseColumns.map((column) => (
                              <MenuItem key={column} value={column}>{column}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Operation</InputLabel>
                        <Select
                          value={action.formula_config?.operation || ''}
                          onChange={(e) => syncArithmeticConfig({ operation: e.target.value })}
                          label="Operation"
                        >
                          {getArithmeticOperationOptions().map((operation) => (
                            <MenuItem key={operation.value} value={operation.value}>{operation.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Operand Source</InputLabel>
                        <Select
                          value={action.formula_config?.operand_source || 'static'}
                          onChange={(e) => syncArithmeticConfig({ operand_source: e.target.value })}
                          label="Operand Source"
                        >
                          <MenuItem value="static">Fixed Value</MenuItem>
                          <MenuItem value="column">Column</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      {action.formula_config?.operand_source === 'column' ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>Operand Column</InputLabel>
                          <Select
                            value={action.formula_config?.operand_column || ''}
                            onChange={(e) => syncArithmeticConfig({ operand_column: e.target.value })}
                            label="Operand Column"
                          >
                            <MenuItem value="">
                              <em>Select Column</em>
                            </MenuItem>
                            {arithmeticOperandColumns.map((column) => (
                              <MenuItem key={column} value={column}>{column}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        renderLiteralInput(
                          arithmeticOperandType,
                          arithmeticOperandType === 'Date' ? 'Operand Date' : (targetType === 'Date' ? 'Days' : 'Value'),
                          action.formula_config?.operand,
                          (nextValue) => syncArithmeticConfig({ operand: nextValue })
                        )
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info" icon={<InfoOutlinedIcon />}>
                        {targetType === 'Date'
                          ? 'Date targets support add/subtract with a numeric day value.'
                          : arithmeticOperandType === 'Date'
                            ? 'This configuration subtracts one date from another and returns a day count.'
                            : 'Number targets support arithmetic with fixed values or other numeric columns.'}
                      </Alert>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {effectiveTargetColumn && action.type === 'concatenate' && (
        targetType === 'Text' ? (
          <>
            <Autocomplete
              multiple
              size="small"
              options={otherColumns}
              value={action.source_columns || []}
              onChange={(_, newValue) => updateAction({ source_columns: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Columns to Concatenate" sx={{ mb: 2 }} />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Separator"
                  value={action.separator || ''}
                  onChange={(e) => updateAction({ separator: e.target.value })}
                  placeholder="-"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transform</InputLabel>
                  <Select
                    value={action.transformation || ''}
                    onChange={(e) => updateAction({ transformation: e.target.value })}
                    label="Transform"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="uppercase">Uppercase</MenuItem>
                    <MenuItem value="lowercase">Lowercase</MenuItem>
                    <MenuItem value="trim">Trim</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Concatenate is only valid for Text columns.
          </Alert>
        )
      )}

      {effectiveTargetColumn && action.type === 'transform_value' && (
        transformOptions.length > 0 ? (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Transformation</InputLabel>
              <Select
                value={action.transformation || ''}
                onChange={(e) => updateAction({ transformation: e.target.value })}
                label="Transformation"
              >
                {transformOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {action.transformation === 'remove_characters' && (
              <TextField
                fullWidth
                size="small"
                label="Characters to remove"
                value={action.transformation_params?.characters || ''}
                onChange={(e) => updateAction({
                  transformation_params: {
                    ...(action.transformation_params || {}),
                    characters: e.target.value,
                  },
                })}
                placeholder="e.g., -/ \\t"
                sx={{ mb: 2 }}
              />
            )}

            {(action.transformation === 'left' || action.transformation === 'right') && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Count"
                value={action.transformation_params?.count || ''}
                onChange={(e) => updateAction({
                  transformation_params: {
                    ...(action.transformation_params || {}),
                    count: parseInt(e.target.value, 10) || 0,
                  },
                })}
                sx={{ mb: 2 }}
              />
            )}

            {action.transformation === 'mid' && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Start (1-based)"
                    value={action.transformation_params?.start || ''}
                    onChange={(e) => updateAction({
                      transformation_params: {
                        ...(action.transformation_params || {}),
                        start: parseInt(e.target.value, 10) || 1,
                      },
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Length"
                    value={action.transformation_params?.length || ''}
                    onChange={(e) => updateAction({
                      transformation_params: {
                        ...(action.transformation_params || {}),
                        length: parseInt(e.target.value, 10) || 0,
                      },
                    })}
                  />
                </Grid>
              </Grid>
            )}

            {action.transformation === 'replace' && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Find"
                    value={action.transformation_params?.find || ''}
                    onChange={(e) => updateAction({
                      transformation_params: {
                        ...(action.transformation_params || {}),
                        find: e.target.value,
                      },
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Replace with"
                    value={action.transformation_params?.replace || ''}
                    onChange={(e) => updateAction({
                      transformation_params: {
                        ...(action.transformation_params || {}),
                        replace: e.target.value,
                      },
                    })}
                  />
                </Grid>
              </Grid>
            )}

            {(action.transformation === 'pad_left' || action.transformation === 'pad_right') && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Target Length"
                    value={action.transformation_params?.length || ''}
                    onChange={(e) => updateAction({
                      transformation_params: {
                        ...(action.transformation_params || {}),
                        length: parseInt(e.target.value, 10) || 0,
                      },
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Pad Character"
                    value={action.transformation_params?.pad_char || ''}
                    onChange={(e) => updateAction({
                      transformation_params: {
                        ...(action.transformation_params || {}),
                        pad_char: e.target.value,
                      },
                    })}
                    placeholder="0"
                  />
                </Grid>
              </Grid>
            )}

            {(action.transformation === 'drop_prefix' || action.transformation === 'drop_suffix') && (
              <TextField
                fullWidth
                size="small"
                label={action.transformation === 'drop_prefix' ? 'Prefix to drop' : 'Suffix to drop'}
                value={action.transformation_params?.text || ''}
                onChange={(e) => updateAction({
                  transformation_params: {
                    ...(action.transformation_params || {}),
                    text: e.target.value,
                  },
                })}
                sx={{ mb: 2 }}
              />
            )}

            {action.transformation === 'round_number' && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Decimal Places"
                value={action.transformation_params?.decimals || 0}
                onChange={(e) => updateAction({
                  transformation_params: {
                    ...(action.transformation_params || {}),
                    decimals: parseInt(e.target.value, 10) || 0,
                  },
                })}
                sx={{ mb: 2 }}
              />
            )}

            {action.transformation === 'format_date' && (
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                Dates will be normalized to YYYY-MM-DD.
              </Alert>
            )}

            {action.transformation === 'cast_number_string' && (
              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                Converts the value to a number and back to text, which is useful for dropping leading zeros.
              </Alert>
            )}
          </>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No transformations are available for {targetType} columns.
          </Alert>
        )
      )}
    </Box>
  );
};

export default CellActionBuilder;
